import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface CarrierOffer {
  id: string;
  load_id: string;
  rate_amount: number;
  status: string;
  expires_at: string | null;
  created_at: string;
  load?: {
    load_number: string;
    origin_city: string;
    origin_state: string;
    destination_city: string;
    destination_state: string;
    pickup_date: string;
    equipment_type: string;
  };
}

const fetchCarrierOffers = async (userId: string): Promise<CarrierOffer[]> => {
  const { data, error } = await supabase
    .from('offers')
    .select(`
      id, load_id, rate_amount, status, expires_at, created_at,
      loads (load_number, origin_city, origin_state, destination_city, destination_state, pickup_date, equipment_type)
    `)
    .eq('created_by', userId)
    .in('status', ['pending', 'accepted_by_carrier', 'countered', 'accepted', 'rejected'])
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) throw error;

  return (data || []).map((offer: any) => ({
    id: offer.id,
    load_id: offer.load_id,
    rate_amount: offer.rate_amount,
    status: offer.status,
    expires_at: offer.expires_at,
    created_at: offer.created_at,
    load: offer.loads ? {
      load_number: offer.loads.load_number,
      origin_city: offer.loads.origin_city,
      origin_state: offer.loads.origin_state,
      destination_city: offer.loads.destination_city,
      destination_state: offer.loads.destination_state,
      pickup_date: offer.loads.pickup_date,
      equipment_type: offer.loads.equipment_type,
    } : undefined,
  }));
};

export function useCarrierOffers() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: offers = [], isLoading: loading } = useQuery({
    queryKey: ['carrier-offers', user?.id],
    queryFn: () => fetchCarrierOffers(user!.id),
    enabled: !!user,
    staleTime: 30_000,
  });

  const pendingLoadIds = useMemo(() => new Set(offers.map(o => o.load_id)), [offers]);

  const acceptLoad = async (loadId: string, rateAmount: number, carrierCompanyId: string) => {
    try {
      const { data, error } = await supabase.rpc('carrier_accept_load', {
        p_load_id: loadId,
        p_carrier_company_id: carrierCompanyId,
        p_rate_amount: rateAmount,
        p_expires_minutes: 30,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; offer_id?: string };

      if (!result.success) {
        toast({ variant: 'destructive', title: 'Cannot accept load', description: result.error || 'An error occurred' });
        return false;
      }

      toast({ title: 'Rate accepted!', description: 'Waiting for broker confirmation. You will be notified when they respond.' });
      await queryClient.invalidateQueries({ queryKey: ['carrier-offers', user?.id] });
      return true;
    } catch (error: any) {
      console.error('Error accepting load:', error);
      toast({ variant: 'destructive', title: 'Error', description: error.message || 'Failed to accept load' });
      return false;
    }
  };

  const hasAcceptedLoad = (loadId: string) => pendingLoadIds.has(loadId);
  const getOfferForLoad = (loadId: string) => offers.find(o => o.load_id === loadId);

  return {
    offers,
    loading,
    acceptLoad,
    hasAcceptedLoad,
    getOfferForLoad,
    refetch: () => queryClient.invalidateQueries({ queryKey: ['carrier-offers', user?.id] }),
  };
}
